import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Calendar, TrendingUp, Bell, Users, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LandingHeader } from "@/components/landing/LandingHeader";
import HeroBackground from "@/components/landing/HeroBackground";

export function HeroSection() {
  const [email, setEmail] = useState("");

  const weekDays = ["M", "T", "W", "T", "F", "S", "S"];
  const highlightedDays = [0, 2, 4]; // Mon, Wed, Fri

  return (
    <section className="relative overflow-hidden min-h-[100dvh] flex flex-col">
      {/* Background gradient */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary/5 via-background to-background" />
      <HeroBackground />
      <LandingHeader />

      {/* Hero Content */}
      <div className="container px-4 flex-1 flex items-center py-6 lg:py-0">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center w-full">
          {/* Left side - Text content */}
          <div className="max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              Free scheduling software for every business
            </h1>
            <p className="mt-3 sm:mt-4 lg:mt-6 text-base lg:text-lg text-muted-foreground">
              Organize your business with 24/7 automated online booking, reminders, payments, and more. Perfect for salons, clinics, consultants, and service professionals.
            </p>
            
            {/* Email signup form */}
            <div className="mt-4 sm:mt-6 lg:mt-8 mb-4 sm:mb-0 flex flex-col sm:flex-row gap-3 sm:justify-center lg:justify-start">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 text-base"
              />
              <Button asChild size="lg" className="h-12 px-8 bg-primary hover:bg-primary/90 btn-glass-shine">
                <Link to={`/signup/business${email ? `?email=${encodeURIComponent(email)}` : ""}`}>
                  Start Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Trust badges */}
            <div className="mt-3 sm:mt-4 lg:mt-6 mb-6 sm:mb-4 lg:mb-0 flex flex-wrap items-center gap-2 justify-center lg:justify-start">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-5 h-5 text-green-500 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <span className="text-sm font-medium">Excellent</span>
              <span className="text-xs text-muted-foreground">Trusted by service businesses worldwide</span>
            </div>
          </div>

          {/* Right side - Rich floating UI cards */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg h-[380px]">
              {/* Revenue card - left middle */}
              <div className="absolute top-4 left-0 animate-float bg-card border rounded-2xl shadow-xl p-5 z-10 w-[220px]">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-foreground">Revenue</span>
                  </div>
                  <span className="text-lg font-bold text-foreground">+24%</span>
                </div>
                <svg viewBox="0 0 180 50" className="w-full h-10 mt-2" fill="none">
                  <polyline
                    points="0,40 25,35 50,38 75,25 100,28 125,15 150,18 180,5"
                    stroke="hsl(var(--primary))"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-dash"
                    style={{ strokeDasharray: 300, strokeDashoffset: 300 }}
                  />
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                  </linearGradient>
                  <polygon
                    points="0,40 25,35 50,38 75,25 100,28 125,15 150,18 180,5 180,50 0,50"
                    fill="url(#revGrad)"
                    className="animate-fade-in"
                    style={{ animationDelay: '2s' }}
                  />
                </svg>
              </div>

              {/* Booking Confirmed card - top right */}
              <div className="absolute top-0 right-0 animate-float [animation-delay:200ms] bg-card border rounded-2xl shadow-xl p-5 z-10 w-[260px]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Booking Confirmed</p>
                    <p className="text-xs text-muted-foreground">Sarah M. — Haircut, 2:30 PM</p>
                  </div>
                </div>
              </div>

              {/* This Week card - bottom left */}
              <div className="absolute top-[180px] left-0 animate-float [animation-delay:400ms] bg-card border rounded-2xl shadow-xl p-5 z-10 w-[240px]">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold text-foreground">This Week</p>
                </div>
                <div className="flex gap-2 mb-1.5">
                  {weekDays.map((day, i) => (
                    <div key={i} className="w-7 text-center text-[10px] font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  {[12, 13, 14, 15, 16, 17, 18].map((date, i) => (
                    <div
                      key={i}
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                        [1, 3, 4].includes(i)
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {date}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notifications card - middle right */}
              <div className="absolute top-[160px] right-4 animate-float [animation-delay:600ms] bg-card border rounded-2xl shadow-xl p-4 z-10">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Bell className="w-5 h-5 text-primary" />
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">3</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">Notifications</span>
                </div>
              </div>

              {/* Staff Online card - bottom right */}
              <div className="absolute bottom-4 right-8 animate-float [animation-delay:800ms] bg-card border rounded-2xl shadow-xl p-4 z-10">
                <p className="text-xs font-medium text-muted-foreground mb-2">Staff Online</p>
                <div className="flex -space-x-2">
                  {[
                    { letter: "S", bg: "bg-primary" },
                    { letter: "A", bg: "bg-green-500" },
                    { letter: "J", bg: "bg-amber-500" },
                    { letter: "M", bg: "bg-destructive" },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className={`w-8 h-8 rounded-full ${s.bg} text-white text-xs font-bold flex items-center justify-center border-2 border-card`}
                    >
                      {s.letter}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile - Floating UI cards */}
          <div className="lg:hidden flex justify-center">
            <div className="relative w-[320px] sm:w-[500px] h-[240px] sm:h-[320px]">
              {/* Revenue */}
              <div className="absolute top-0 left-0 animate-float bg-card border rounded-xl shadow-lg p-3 z-10 w-[160px] sm:w-[180px]">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-xs font-semibold text-foreground">Revenue</span>
                  </div>
                  <span className="text-sm font-bold text-foreground">+24%</span>
                </div>
                <svg viewBox="0 0 140 35" className="w-full h-7 mt-1" fill="none">
                  <polyline
                    points="0,30 20,26 40,29 60,18 80,21 100,10 120,13 140,3"
                    stroke="hsl(var(--primary))"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="animate-dash"
                    style={{ strokeDasharray: 250, strokeDashoffset: 250 }}
                  />
                </svg>
              </div>

              {/* Booking Confirmed */}
              <div className="absolute top-0 right-0 sm:right-4 animate-float [animation-delay:200ms] bg-card border rounded-xl shadow-lg p-3 z-10 w-[150px] sm:w-[170px]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground">Confirmed</p>
                    <p className="text-[10px] text-muted-foreground">Sarah — 2:30 PM</p>
                  </div>
                </div>
              </div>

              {/* This Week */}
              <div className="absolute top-[110px] sm:top-[140px] left-0 sm:left-4 animate-float [animation-delay:400ms] bg-card border rounded-xl shadow-lg p-3 z-10">
                <div className="flex items-center gap-1.5 mb-2">
                  <Calendar className="w-3.5 h-3.5 text-primary" />
                  <p className="text-xs font-semibold text-foreground">This Week</p>
                </div>
                <div className="flex gap-1.5 mb-1">
                  {weekDays.map((day, i) => (
                    <div key={i} className="w-5 text-center text-[8px] font-medium text-muted-foreground">{day}</div>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  {[12, 13, 14, 15, 16, 17, 18].map((date, i) => (
                    <div
                      key={i}
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium ${
                        [1, 3, 4].includes(i) ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {date}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notifications */}
              <div className="absolute top-[120px] sm:top-[150px] right-2 sm:right-8 animate-float [animation-delay:600ms] bg-card border rounded-xl shadow-lg p-2.5 z-10">
                <div className="flex items-center gap-1.5">
                  <div className="relative">
                    <Bell className="w-4 h-4 text-primary" />
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-destructive text-destructive-foreground text-[8px] font-bold flex items-center justify-center">3</span>
                  </div>
                  <span className="text-[10px] font-semibold text-foreground">Alerts</span>
                </div>
              </div>

              {/* Staff Online */}
              <div className="absolute bottom-6 sm:bottom-2 left-8 sm:left-16 animate-float [animation-delay:800ms] bg-card border rounded-xl shadow-lg p-3 z-10 hidden sm:block">
                <p className="text-[10px] font-medium text-muted-foreground mb-1.5">Staff Online</p>
                <div className="flex -space-x-1.5">
                  {[
                    { letter: "S", bg: "bg-primary" },
                    { letter: "A", bg: "bg-green-500" },
                    { letter: "J", bg: "bg-amber-500" },
                    { letter: "M", bg: "bg-destructive" },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className={`w-6 h-6 rounded-full ${s.bg} text-white text-[9px] font-bold flex items-center justify-center border-2 border-card`}
                    >
                      {s.letter}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
